import { useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { PlayCircle, Loader2, TrendingUp, TrendingDown, Target, Activity } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, ReferenceLine } from "recharts";
import { fmtMoney, fmtPct } from "../lib/api";

const STRATEGIES = [
  { v: "rsi_meanrev", label: "RSI Mean Reversion", hint: "Buy when RSI < 30, sell when RSI > 70" },
  { v: "macd_cross", label: "MACD Crossover", hint: "Buy on bullish MACD/signal cross, sell on bearish" },
  { v: "sma_cross", label: "SMA 20/50 Cross", hint: "Buy when 20 crosses above 50 (golden), sell on death cross" },
  { v: "momentum", label: "5-Bar Momentum", hint: "Buy on ≥1% 5-bar move, sell on ≤-1%" },
  { v: "buy_hold", label: "Buy & Hold", hint: "Baseline — buy at start, hold to end" },
];

export default function Backtest() {
  const [assetType, setAssetType] = useState("stock");
  const [symbol, setSymbol] = useState("AAPL");
  const [strategy, setStrategy] = useState("rsi_meanrev");
  const [range, setRange] = useState("1Y");
  const [startingCash, setStartingCash] = useState(10000);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [wfRunning, setWfRunning] = useState(false);
  const [walkForward, setWalkForward] = useState(null);
  const [mcRunning, setMcRunning] = useState(false);
  const [monteCarloRes, setMonteCarloRes] = useState(null);

  const runWalkForward = async () => {
    setWfRunning(true); setWalkForward(null);
    try {
      const { data } = await api.post("/backtest/walkforward", {
        asset_type: assetType, symbol: symbol.trim(), strategy, range,
        timeframe: "1d", starting_cash: Number(startingCash), n_windows: 5,
      });
      if (data.error) throw new Error(data.error);
      setWalkForward(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Walk-forward failed");
    } finally { setWfRunning(false); }
  };

  const runMonteCarlo = async () => {
    setMcRunning(true); setMonteCarloRes(null);
    try {
      const { data } = await api.post("/backtest/montecarlo", {
        asset_type: assetType, symbol: symbol.trim(), strategy, range,
        timeframe: "1d", starting_cash: Number(startingCash), n_runs: 500,
      });
      if (data.error) throw new Error(data.error);
      setMonteCarloRes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Monte Carlo failed");
    } finally { setMcRunning(false); }
  };

  const compareAll = async () => {
    setComparing(true); setComparison(null);
    try {
      const { data } = await api.post("/backtest/compare", {
        asset_type: assetType, symbol: symbol.trim(),
        range, timeframe: "1d", starting_cash: Number(startingCash),
      });
      setComparison(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compare failed");
    } finally { setComparing(false); }
  };

  const promote = async () => {
    if (!result) return;
    const name = window.prompt("Name this bot:", `${symbol.toUpperCase()} ${strategy}`);
    if (!name?.trim()) return;
    setPromoting(true);
    try {
      const { data } = await api.post("/backtest/promote", {
        name: name.trim(), asset_type: assetType, symbol: symbol.trim(),
        strategy, starting_cash: Number(startingCash), route: "sim",
      });
      toast.success(`Bot created (${data.mapped_strategy}) — Auto-Trade →`, {
        action: { label: "Open", onClick: () => window.location.assign("/bots") },
        duration: 8000,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally { setPromoting(false); }
  };

  const run = async () => {
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/backtest", {
        asset_type: assetType, symbol: symbol.trim(), strategy,
        range, timeframe: "1d", starting_cash: Number(startingCash),
      });
      if (data.error) toast.error(data.error);
      else setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backtest failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="backtest-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Backtest</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Validate a strategy against historical data before you turn it loose live.
        </p>
      </div>

      <Card className="p-6 border-border rounded-xl">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-1">
            <Label className="text-xs text-muted-foreground mb-1">Asset</Label>
            <div className="flex gap-1">
              {["stock", "crypto"].map((a) => (
                <button key={a} onClick={() => { setAssetType(a); setSymbol(a === "stock" ? "AAPL" : "bitcoin"); }}
                  data-testid={`bt-asset-${a}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${assetType === a ? "bg-[hsl(var(--ai))]/15 border-[hsl(var(--ai))] text-[hsl(var(--ai))]" : "border-border text-muted-foreground"}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs text-muted-foreground mb-1">Symbol</Label>
            <Input value={symbol} onChange={(e) => setSymbol(e.target.value)}
              placeholder={assetType === "stock" ? "AAPL" : "bitcoin"}
              className="uppercase" data-testid="bt-symbol" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-muted-foreground mb-1">Strategy</Label>
            <select value={strategy} onChange={(e) => setStrategy(e.target.value)}
              data-testid="bt-strategy"
              className="w-full bg-background border border-border rounded-md text-sm px-3 py-2 h-9">
              {STRATEGIES.map((s) => (<option key={s.v} value={s.v}>{s.label}</option>))}
            </select>
            <div className="text-[10px] text-muted-foreground mt-1">
              {STRATEGIES.find(s => s.v === strategy)?.hint}
            </div>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs text-muted-foreground mb-1">Range</Label>
            <select value={range} onChange={(e) => setRange(e.target.value)}
              data-testid="bt-range"
              className="w-full bg-background border border-border rounded-md text-sm px-3 py-2 h-9">
              {["1M", "3M", "1Y"].map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          </div>
          <div className="md:col-span-1">
            <Label className="text-xs text-muted-foreground mb-1">Starting $</Label>
            <Input type="number" value={startingCash}
              onChange={(e) => setStartingCash(e.target.value)}
              data-testid="bt-cash" />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2 flex-wrap">
          <Button variant="outline" onClick={runWalkForward} disabled={wfRunning || !symbol.trim()} data-testid="bt-walkforward" className="rounded-full">
            {wfRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Target className="w-4 h-4 mr-1" />}
            {wfRunning ? "Running…" : "Walk-forward"}
          </Button>
          <Button variant="outline" onClick={runMonteCarlo} disabled={mcRunning || !symbol.trim()} data-testid="bt-montecarlo" className="rounded-full">
            {mcRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Activity className="w-4 h-4 mr-1" />}
            {mcRunning ? "Simulating…" : "Monte Carlo"}
          </Button>
          <Button variant="outline" onClick={compareAll} disabled={comparing || !symbol.trim()} data-testid="bt-compare" className="rounded-full">
            {comparing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Activity className="w-4 h-4 mr-1" />}
            {comparing ? "Comparing…" : "Compare all strategies"}
          </Button>
          <Button onClick={run} disabled={running || !symbol.trim()} data-testid="bt-run">
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
            {running ? "Running…" : "Run backtest"}
          </Button>
        </div>
      </Card>

      {walkForward && (
        <Card className="p-6 border-border rounded-xl" data-testid="bt-wf-result">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display font-semibold">Walk-forward · {walkForward.symbol?.toUpperCase()}</h3>
              <div className="text-xs text-muted-foreground mt-0.5">
                {walkForward.n_windows} sequential windows · mean {walkForward.mean_return_pct}% · σ {walkForward.stddev_return_pct}% · {walkForward.positive_rate_pct}% profitable
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setWalkForward(null)} data-testid="bt-wf-close">Close</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground border-b border-border">
                <tr><th className="py-2 pr-2">#</th><th className="py-2 pr-2">Return</th><th className="py-2 pr-2 text-right">Sharpe</th><th className="py-2 pr-2 text-right">Max DD</th><th className="py-2 pr-2 text-right">Trades</th></tr>
              </thead>
              <tbody>
                {walkForward.windows?.map((w) => (
                  <tr key={w.index} className="border-b border-border/50" data-testid={`bt-wf-row-${w.index}`}>
                    <td className="py-2 pr-2 font-mono-tab">#{w.index}</td>
                    <td className={`py-2 pr-2 font-mono-tab ${w.return_pct >= 0 ? "gain" : "loss"}`}>{w.return_pct >= 0 ? "+" : ""}{w.return_pct.toFixed(2)}%</td>
                    <td className="py-2 pr-2 text-right font-mono-tab">{w.sharpe}</td>
                    <td className="py-2 pr-2 text-right font-mono-tab loss">{w.max_dd_pct}%</td>
                    <td className="py-2 pr-2 text-right font-mono-tab">{w.n_trades}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {monteCarloRes && (
        <Card className="p-6 border-border rounded-xl" data-testid="bt-mc-result">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display font-semibold">Monte Carlo · {monteCarloRes.symbol?.toUpperCase()}</h3>
              <div className="text-xs text-muted-foreground mt-0.5">
                {monteCarloRes.n_runs} bootstrap runs · {monteCarloRes.loss_probability_pct}% chance of ending below starting cash
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setMonteCarloRes(null)} data-testid="bt-mc-close">Close</Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <MCStat label="p5" val={`${monteCarloRes.p5_return_pct}%`} cls="loss" />
            <MCStat label="median" val={`${monteCarloRes.median_return_pct}%`} />
            <MCStat label="p95" val={`${monteCarloRes.p95_return_pct}%`} cls="gain" />
            <MCStat label="worst" val={`$${monteCarloRes.worst_final.toLocaleString()}`} />
          </div>
        </Card>
      )}

      {comparison && (
        <Card className="p-6 border-border rounded-xl" data-testid="bt-comparison">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-display font-semibold">Strategy comparison · {comparison.symbol.toUpperCase()}</h3>
              <div className="text-xs text-muted-foreground mt-0.5">{comparison.candle_count} candles · {comparison.range} · ranked by total return</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setComparison(null)} data-testid="bt-comparison-close">Close</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="bt-comparison-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-2">Rank</th>
                  <th className="py-2 pr-2">Strategy</th>
                  <th className="py-2 pr-2 text-right">Return</th>
                  <th className="py-2 pr-2 text-right">Final</th>
                  <th className="py-2 pr-2 text-right">Trades</th>
                  <th className="py-2 pr-2 text-right">Win %</th>
                  <th className="py-2 pr-2 text-right">Sharpe</th>
                  <th className="py-2 pr-2 text-right">Max DD</th>
                  <th className="py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map((r, i) => (
                  <tr key={r.strategy} className={`border-b border-border/50 ${i === 0 ? "bg-[hsl(var(--gain))]/5" : ""}`} data-testid={`bt-comparison-row-${r.strategy}`}>
                    <td className="py-2 pr-2 font-mono-tab">#{i+1}{i===0 && " 🏆"}</td>
                    <td className="py-2 pr-2">{STRATEGIES.find(s => s.v === r.strategy)?.label || r.strategy}</td>
                    <td className={`py-2 pr-2 text-right font-mono-tab ${r.total_return_pct >= 0 ? "gain" : "loss"}`}>
                      {r.total_return_pct >= 0 ? "+" : ""}{r.total_return_pct.toFixed(2)}%
                    </td>
                    <td className="py-2 pr-2 text-right font-mono-tab">{fmtMoney(r.final_equity)}</td>
                    <td className="py-2 pr-2 text-right font-mono-tab">{r.n_trades}</td>
                    <td className="py-2 pr-2 text-right font-mono-tab">{r.win_rate_pct}%</td>
                    <td className="py-2 pr-2 text-right font-mono-tab">{r.sharpe}</td>
                    <td className="py-2 pr-2 text-right font-mono-tab loss">{r.max_drawdown_pct}%</td>
                    <td className="py-2 pr-2 text-right">
                      <button
                        onClick={() => { setStrategy(r.strategy); setResult(null); setTimeout(run, 100); }}
                        data-testid={`bt-comparison-pick-${r.strategy}`}
                        className="text-[hsl(var(--ai))] hover:underline text-[10px] uppercase tracking-wider"
                      >Run detail →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="bt-stats">
            <StatCard label="Final equity" value={fmtMoney(result.final_equity)} testid="bt-stat-equity" />
            <StatCard label="Total return" value={`${result.total_return_pct >= 0 ? "+" : ""}${result.total_return_pct.toFixed(2)}%`}
              tone={result.total_return_pct >= 0 ? "gain" : "loss"} testid="bt-stat-return" />
            <StatCard label="Buy & hold" value={`${result.buy_hold_return_pct >= 0 ? "+" : ""}${result.buy_hold_return_pct.toFixed(2)}%`}
              tone={result.buy_hold_return_pct >= 0 ? "gain" : "loss"} testid="bt-stat-bh" />
            <StatCard label="Trades" value={result.n_trades} testid="bt-stat-trades" />
            <StatCard label="Win rate" value={`${result.win_rate_pct}%`} testid="bt-stat-winrate" />
            <StatCard label="Sharpe" value={result.sharpe} testid="bt-stat-sharpe" />
          </div>

          {/* Promote strategy to live bot */}
          <div className="flex items-center justify-between flex-wrap gap-3 rounded-xl p-4 border border-[hsl(var(--gain))]/30 bg-[hsl(var(--gain))]/5">
            <div className="text-sm">
              <div className="font-medium text-foreground">
                Happy with the results? Promote this strategy into an auto-trade bot.
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Uses the same {result.strategy} config on {symbol.toUpperCase()} with paper (sim) routing.
              </div>
            </div>
            <Button onClick={promote} disabled={promoting} data-testid="bt-promote" className="rounded-full">
              {promoting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Target className="w-4 h-4 mr-1" />}
              Save as Auto-Trade bot
            </Button>
          </div>

          <Card className="p-6 border-border rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">Equity curve</h3>
              <div className="text-xs text-muted-foreground">
                Max drawdown: <span className="loss font-mono-tab">{result.max_drawdown_pct}%</span> · {result.candle_count} candles
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={result.equity_curve}>
                <defs>
                  <linearGradient id="eq-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--ai))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--ai))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" opacity={0.3} />
                <XAxis dataKey="t" tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`}
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                  labelFormatter={(t) => new Date(t).toLocaleDateString()}
                  formatter={(v) => [fmtMoney(v), "Equity"]} />
                <ReferenceLine y={result.starting_cash} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 3" opacity={0.4} />
                <Area type="monotone" dataKey="equity" stroke="hsl(var(--ai))" strokeWidth={1.5} fill="url(#eq-grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {result.trades?.length > 0 && (
            <Card className="p-6 border-border rounded-xl" data-testid="bt-trades">
              <h3 className="font-display font-semibold mb-3">Trades ({result.trades.length})</h3>
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {result.trades.map((t, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 text-xs py-1.5 border-b border-border/50 items-center">
                    <span className="text-muted-foreground font-mono-tab">{new Date(t.t).toLocaleDateString()}</span>
                    <span className={`text-[10px] font-medium uppercase tracking-widest rounded px-1.5 py-0.5 w-fit ${t.side === "buy" ? "text-[hsl(var(--gain))] bg-[hsl(var(--gain))]/10" : "text-[hsl(var(--loss))] bg-[hsl(var(--loss))]/10"}`}>
                      {t.side}
                    </span>
                    <span className="font-mono-tab">${t.price.toFixed(2)} × {t.qty}</span>
                    {t.pnl != null ? (
                      <span className={`text-right font-mono-tab ${t.pnl >= 0 ? "gain" : "loss"}`}>
                        {t.pnl >= 0 ? "+" : ""}{fmtMoney(t.pnl)}
                      </span>
                    ) : <span />}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  return (
    <Card className="p-4 border-border rounded-xl" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-mono-tab text-lg font-semibold mt-1 ${tone === "gain" ? "gain" : tone === "loss" ? "loss" : ""}`}>
        {value}
      </div>
    </Card>
  );
}


const MCStat = ({ label, val, cls = "" }) => (
  <div className="p-3 rounded-lg border border-border">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`font-mono-tab text-lg ${cls}`}>{val}</div>
  </div>
);
